import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { gameService } from '../../services/gameService';
import { GameSpaceInfo } from '../../types';

interface GameSpaceRouteProps {
  children: React.ReactNode;
  requirePro?: boolean;
  requireSpecificGameSpace?: boolean;
}

export default function GameSpaceRoute({ 
  children, 
  requirePro = false,
  requireSpecificGameSpace = false
}: GameSpaceRouteProps) {
  const [loading, setLoading] = useState(true);
  const [gameSpaces, setGameSpaces] = useState<GameSpaceInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const loadGameSpaces = async () => {
      try {
        const response = await gameService.getUserGameSpaces();
        if (response.success) {
          setGameSpaces(response.data || []);
        } else {
          setError(response.message || 'Failed to load game spaces');
        }
      } catch (err) {
        console.error('Error loading game spaces:', err);
        setError('An unexpected error occurred while loading game spaces');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadGameSpaces();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login?returnUrl=/gamespaces" replace />;
  }

  // Show loading while game spaces are being fetched
  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading game spaces...</span>
            </Spinner>
            <p className="mt-2 text-muted">Loading game spaces...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // If Member access is required but user is not Member
  if (requirePro && user?.userType !== 'Member') {
    return <Navigate to="/pro" replace />;
  }

  // Handle specific GameSpace requirements
  if (requireSpecificGameSpace) {
    // If no GameSpaces exist at all
    if (gameSpaces.length === 0) {
      return <Navigate to="/create-gamespace" replace />;
    }

    // Check if we're on the base /gamespace route with no ID
    const path = window.location.pathname;
    const isBaseGameSpacePath = path === '/gamespace';
    
    // Only redirect Player users when they're on the base /gamespace path (no ID)
    if (isBaseGameSpacePath && user?.userType === 'Player' && gameSpaces.length > 0) {
      return <Navigate to={`/gamespace/${gameSpaces[0].id}`} replace />;
    }
  }

  return <>{children}</>;
}
